
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component<Tabs>({})
export default class Tabs extends Vue {
	@Prop({ type: Array, required: true, default: () => [] }) private tabs!: Tab[];
	@Prop({ type: String, default: '' }) private initialTab!: string;
	@Prop({ type: Boolean, default: true }) private syncHash!: boolean;
	@Prop({ type: String, default: 'tab' }) private idPrefix!: string;

	/**
	 * Keep the active tab in sync with the URL hash so route changes (e.g. a redirect to
	 * `/menus#edit-menus`, or browser back/forward) switch tabs. Guarded to no-op when the hash
	 * already matches the active tab, avoiding a loop with the activeTabKey watcher's own hash write.
	 *
	 * @param {string} hash - the new route hash (e.g. '#edit-menus')
	 * @return {void}
	 */
	@Watch('$route.hash')
	private onRouteHashChange(hash: string): void {
		// Disable to use the tabs as a standalone control without reading/writing the URL hash
		if (!this.syncHash) {
			return;
		}
		const key: string = (hash || '').replace('#', '');
		const target: Tab | undefined = this.tabs.find((tab: Tab) => tab.key === key) ?? this.tabs[0];
		if (target && target.key !== this.activeTabKey && !target.hidden) {
			this.setTab(target.key);
		}
	}

	@Watch('activeTabKey')
	private onActiveTabKeyChange(key: string): void {
		const tab: Tab | undefined = this.findVisibleTab(key);
		if (tab) {
			if (this.syncHash) {
				// Use replaceState instead of window.location.hash so the URL hash updates doesn't trigger the browser's scroll
				history.replaceState(history.state, '', `#${tab.key}`);
			}
			this.$emit('change', tab);
		}
	}

	// Set before watchers are installed to prevent a 'change' emit
	public activeTabKey: string = this.getInitialTabKey();

	public setTab(key: string): void {
		if (this.findVisibleTab(key)) {
			this.activeTabKey = key;
		}
	}

	private getInitialTabKey(): string {
		return this.findVisibleTab(this.initialTab || this.tabs[0]?.key)?.key ?? '';
	}

	/**
	 * Resolve a key to its tab, rejecting unknown keys and hidden tabs
	 *
	 * @param {string | undefined} key
	 * @return {Tab | undefined}
	 */
	private findVisibleTab(key: string | undefined): Tab | undefined {
		const tab: Tab | undefined = this.tabs.find((currentTab: Tab) => currentTab.key === key);

		return tab && !tab.hidden ? tab : undefined;
	}
}
